import { useCallback, useEffect, useState } from 'react';
import { Loader, Modal } from '../../../../components';
import TransactionLabelField from '../../../../components/transaction/TransactionLabelField';
import { MarketPlaceBreederPaymentTransaction } from '../../../../types/interfaces/marketPlace.interfaces';
import { showToast } from '../../../../services/toast.service';
import { centsToDollars } from '../../../../services/account.service';
import { getMarketplaceBreederPaymentTransactionById } from '../../../../api/MarketPlaceBreederPaymentTransactionApi';
import { toastMessages } from '../../../../constants/errorMessages';
import {
  LABEL_CONSTANTS,
  BUTTON_CONSTANTS,
  BREEDER_PAYMENTS_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../../../constants/common';

export type BreederPaymentDetailsModalProps = {
  isOpen: boolean;
  width?: number;
  onClose: () => void;
  breederPaymentTransactionId?: number | null;
};

const BreederPaymentDetailsModal = ({
  isOpen,
  width = 600,
  onClose,
  breederPaymentTransactionId,
}: BreederPaymentDetailsModalProps) => {
  const [breederDetails, setBreederDetails] = useState<MarketPlaceBreederPaymentTransaction>();
  const [isLoading, setIsLoading] = useState(true);

  const loadDetails = useCallback(async () => {
    if (breederPaymentTransactionId) {
      try {
        setIsLoading(true);
        const { data } = await getMarketplaceBreederPaymentTransactionById(breederPaymentTransactionId, { include: 'Account' });
        setBreederDetails(data ?? null);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    }
  }, [breederPaymentTransactionId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} width={width} ignoreBackdrop>
        <div className="payment-history-details-modal">
          {isLoading ? (
            <Loader loaderSize="small" pageLoader />
          ) : (
            <div className="payment-history-details">
              <div className="payment-history-action card">
                <h2>{BREEDER_PAYMENTS_CONSTANTS.PAYMENT_DETAILS}</h2>
                <TransactionLabelField
                  label={`${BREEDER_PAYMENTS_CONSTANTS.TRANSACTION_REFERENCE_ID}:`}
                  value={<label>{breederDetails?.transactionReferenceId ?? '--'}</label>}
                />
                <TransactionLabelField
                  label={`${TABLE_HEADER_CONSTANTS.BREEDER_NAME}:`}
                  value={<label>{breederDetails?.account?.name ?? '--'}</label>}
                />
                <TransactionLabelField
                  label={`${BREEDER_PAYMENTS_CONSTANTS.PAID_DATE}:`}
                  value={
                    <label>
                      {breederDetails?.billedDate ? new Date(breederDetails.billedDate).toLocaleDateString('en-US') : '--'}
                    </label>
                  }
                />
                <TransactionLabelField
                  label={`${TABLE_HEADER_CONSTANTS.ORDERS}:`}
                  value={<label>{breederDetails?.numberOfOrders ?? '--'}</label>}
                />
                <TransactionLabelField
                  label={`${TABLE_HEADER_CONSTANTS.GROSS_AMOUNT}:`}
                  value={<label>{`$${centsToDollars(breederDetails?.totalAmountCents ?? 0)}`}</label>}
                />
                <TransactionLabelField
                  label={`${TABLE_HEADER_CONSTANTS.COMMISSION}:`}
                  value={<label>{`$${centsToDollars(breederDetails?.rsgCommissionCents ?? 0)}`}</label>}
                />
                <TransactionLabelField
                  label={`${TABLE_HEADER_CONSTANTS.NET_PAYABLE}:`}
                  value={<label>{`$${centsToDollars(breederDetails?.paidAmountCents ?? 0)}`}</label>}
                />
                <TransactionLabelField
                  label={`${TABLE_HEADER_CONSTANTS.PAYMENT_STATUS}:`}
                  value={<label>{breederDetails?.paymentStatus ?? '--'}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.PAYMENT_METHOD}:`}
                  value={<label>{breederDetails?.paymentMethod ?? '--'}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.NOTES}:`}
                  value={<label>{breederDetails?.notes ?? '--'}</label>}
                />
              </div>
              <div className="flex-right">
                <button className="button medium green close-button" onClick={() => onClose()}>
                  {BUTTON_CONSTANTS.CLOSE}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default BreederPaymentDetailsModal;
